import { PlayerEvent, TimedMetadata } from '@yospace/admanagement-sdk';

import { merge, of, EMPTY } from 'rxjs';
import {
  catchError,
  switchMap,
  takeUntil,
  map,
  concatMap,
  endWith,
  takeWhile,
  take,
  startWith,
  filter,
  combineLatestWith,
} from 'rxjs/operators';
import {
  MEDIA_TIMEUPDATE,
  MEDIA_PAUSE,
  MEDIA_PLAY,
  YOSPACE_EMSG,
  MEDIA_VOLUMECHANGE,
  MEDIA_WAITING,
  MEDIA_FINISHED,
  MEDIA_CONTINUE,
  YOSPACE_ID3,
} from '../../core/events';
import { EventObserver } from './eventObserver';
import { DefaultPlaybackPolicy } from './defaultPlaybackPolicy';
import { getLiveSessionManager } from './yospaceManager';
import UnifiedPlayer from '../../core/UnifiedPlayer';
import { EMSGEventArgs, ID3EventArgs } from '../../core/typedEvents';
import { trackPluginCall } from '../../util/overseer/overseer';
import { AdVendor, Media } from '../../types';
import { RobustUnifiedPlayerAdBreakEmitter } from './RobustUnifiedPlayerAdBreakEmitter';

const { START, STALL, CONTINUE, PAUSE, RESUME } = PlayerEvent;

export default () =>
  function yospacePlugin(_: any, player: UnifiedPlayer) {
    const pause$ = player.observe(MEDIA_PAUSE);
    const play$ = player.observe(MEDIA_PLAY);
    const volume$ = player.observe(MEDIA_VOLUMECHANGE);
    const mediaFinished$ = player.observe(MEDIA_FINISHED);
    const media$ = player.observe((data: any) => data.media);
    const buffering$ = player.observe(MEDIA_WAITING);
    const timeupdate$ = player.observe(MEDIA_TIMEUPDATE);
    let playerEmitterInstance: RobustUnifiedPlayerAdBreakEmitter | null = null;

    const destroy$ = player.observe().pipe(endWith('destroy'));

    destroy$.subscribe(() => {
      if (playerEmitterInstance) {
        playerEmitterInstance.destroy();
      }
    });

    const stalled$ = merge(
      buffering$.pipe(
        switchMap(() =>
          timeupdate$.pipe(
            map(e => ({ ...e, type: MEDIA_CONTINUE })),
            take(1),
            startWith({ type: MEDIA_WAITING })
          )
        )
      ),
      pause$,
      play$
    ).pipe(
      startWith({ type: MEDIA_CONTINUE }),
      takeWhile(() => !player.ended)
    );

    const timeUpdateWithStalled$ = player.observe(MEDIA_TIMEUPDATE).pipe(
      combineLatestWith(stalled$),
      filter(([, stalled]) => [MEDIA_CONTINUE, MEDIA_PLAY].includes(stalled.type)),
      map(([timeupdate]) => timeupdate)
    );

    const emsgs: (EMSGEventArgs | ID3EventArgs)[] = [];

    merge(player.observe(YOSPACE_EMSG), player.observe(YOSPACE_ID3)).subscribe(emsg => {
      emsgs.push(emsg);
    });
    const TIMED_METADATA = 'timedmetadata' as const;
    const emsg$ = timeUpdateWithStalled$.pipe(
      map(() => {
        const events = emsgs.filter(emsg => emsg.startTime < player.currentTime);
        events.forEach(f =>
          emsgs.splice(
            emsgs.findIndex(e => e.startTime === f.startTime),
            1
          )
        );
        return { type: TIMED_METADATA, events };
      }),
      filter(({ events }) => events.length > 0)
    );

    media$
      .pipe(
        catchError(() => EMPTY),
        switchMap((media: Media) =>
          (media?.metadata?.isLive &&
          (media?.ads?.vendor === AdVendor.YOSPACE_CS || media?.ssai?.integration === 'yospace')
            ? of(media)
            : EMPTY
          ).pipe(
            concatMap(async () => {
              const sessionManager = await getLiveSessionManager(media);
              if (sessionManager !== null) {
                if (playerEmitterInstance) {
                  playerEmitterInstance.destroy();
                }
                playerEmitterInstance = new RobustUnifiedPlayerAdBreakEmitter(player, {
                  forceStopAdBreakEnabled: true,
                });

                sessionManager.addAnalyticObserver(new EventObserver(playerEmitterInstance));
                sessionManager.setPlaybackPolicyHandler(new DefaultPlaybackPolicy());
                sessionManager.onPlayerEvent(START, 0);
              }
              return sessionManager;
            }),
            switchMap(sessionManager =>
              sessionManager === null
                ? EMPTY
                : merge(stalled$, volume$, timeUpdateWithStalled$, emsg$).pipe(
                    map(event => ({ sessionManager, event })),
                    takeUntil(merge(destroy$, mediaFinished$))
                  )
            )
          )
        )
      )
      .subscribe(({ sessionManager, event }) => {
        const metadata = { source: event };
        switch (event.type) {
          case TIMED_METADATA: {
            event.events.forEach(emsg => {
              const { YMID, YSEQ, YTYP, YDUR } = emsg.messageData;
              const meta = TimedMetadata.createFromMetadata(
                YMID,
                YSEQ,
                YTYP,
                YDUR,
                emsg.startTime * 1000
              );
              if (meta) sessionManager.onTimedMetadata(meta);
              trackPluginCall('Yospace', 'onTimedMetadata', meta, metadata);
            });
            break;
          }
          case MEDIA_TIMEUPDATE:
            sessionManager.onPlayheadUpdate(player.currentTime);
            trackPluginCall(
              'Yospace',
              'onPlayheadUpdate',
              { currentTime: player.currentTime },
              metadata
            );
            break;
          case MEDIA_WAITING:
            sessionManager.onPlayerEvent(STALL, player.currentTime);
            trackPluginCall(
              'Yospace',
              'onPlayerEvent',
              { event: 'STALL', currentTime: player.currentTime },
              metadata
            );
            break;
          case MEDIA_PAUSE:
            sessionManager.onPlayerEvent(PAUSE, player.currentTime);
            trackPluginCall(
              'Yospace',
              'onPlayerEvent',
              { event: 'PAUSE', currentTime: player.currentTime },
              metadata
            );
            break;
          case MEDIA_PLAY:
            sessionManager.onPlayerEvent(RESUME, player.currentTime);
            trackPluginCall(
              'Yospace',
              'onPlayerEvent',
              { event: 'RESUME', currentTime: player.currentTime },
              metadata
            );
            break;
          case MEDIA_CONTINUE:
            sessionManager.onPlayerEvent(CONTINUE, player.currentTime);
            trackPluginCall(
              'Yospace',
              'onPlayerEvent',
              { event: 'CONTINUE', currentTime: player.currentTime },
              metadata
            );
            break;
          case MEDIA_VOLUMECHANGE:
            sessionManager.onVolumeChange(event.muted);
            trackPluginCall('Yospace', 'onVolumeChange', { muted: event.muted }, metadata);
            break;
          default:
        }
      });

    media$.subscribe();
  };
